import React, { useState, useEffect } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher.js';
import { DataService } from '../../services/dataService';
import './DataLoader.css';
import SelectPrimaryData from './LoadPrimaryData.js';
import SelectBackCheckData from './LoadBackCheckData.js';
import LoadXLSForm from './LoadXLSForm.js';
import TranscribeModal from './TranscribeModal.js';
import AILoadingOverlay from '../common/AILoadingOverlay';
import {
  Database,
  FileSpreadsheet,
  Table,
  CheckCircle,
  CheckCircle2,
  Layers,
  Headphones,
  Download,
  RotateCw,
} from 'lucide-react';
 
// Format a relative time like "2m ago" or "3h ago" from an ISO timestamp.
// Format a relative time like "2m ago" or "3h ago" from an ISO timestamp.
// Returns 'earlier' when iso is null/undefined — used for inferred records
// where we detect prior transcripts but never persisted the timestamp.
const formatRelativeTime = (iso) => {
  if (!iso) return 'earlier';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'earlier';
  const diffMs = Date.now() - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
};
 
// localStorage key for per-project transcription history
const lastTranscriptionKey = (projectId) => `dataquad:lastTranscription:${projectId || 'default'}`;
 
// Detect Tauri desktop app (matches the pattern used in src/index.js)
const isTauri = typeof window !== 'undefined' && Boolean(window.__TAURI_INTERNALS__);
 
// Lazy access to the Tauri API — only reachable when running in desktop.
// Uses ES module imports (Tauri v2's official path) instead of the legacy
// window.__TAURI__ globals, which require withGlobalTauri to be enabled.
let _tauriApiCache = null;
const getTauriApi = () => {
  if (!isTauri) return null;
  if (_tauriApiCache) return _tauriApiCache;
 
  // Note: this returns a Promise on first call. Callers should await getTauriApi().
  return (async () => {
    try {
      const [{ invoke }, { listen }] = await Promise.all([
        import('@tauri-apps/api/core'),
        import('@tauri-apps/api/event'),
      ]);
      _tauriApiCache = { invoke, listen };
      return _tauriApiCache;
    } catch (e) {
      console.error('Failed to load Tauri API:', e);
      return null;
    }
  })();
};
 
const DataLoader = ({ 
  onDataLoaded, 
  onBackcheckDataLoaded,
  onXLSFormLoaded,
  isDataPreviewVisible, 
  onClearPreviousState, 
  projectId, 
  onNavigateToPreview,
  primaryData,
  backcheckData: backcheckDataProp,
  xlsformData: xlsformDataProp,
  initialDatasetType = 'primary' // 'primary', 'xlsform', 'backcheck'
}) => {
  
  const { isDarkMode } = useTheme();
  
  // Derive loaded status from props (persisted data from App.js)
  const primaryDataLoaded = isDataPreviewVisible || (primaryData !== null && primaryData !== undefined);
  const backcheckDataLoaded = backcheckDataProp !== null && backcheckDataProp !== undefined;
  const xlsformLoaded = xlsformDataProp !== null && xlsformDataProp !== undefined;
  
  // Get columns from primary data for XLSForm matching
  const dataColumns = primaryData?.columns || [];
  
  // UI State - use initialDatasetType prop
  const [activeDataset, setActiveDataset] = useState(initialDatasetType); // 'primary', 'xlsform', 'backcheck'
  
  // Sync activeDataset with initialDatasetType prop when it changes
  useEffect(() => {
    setActiveDataset(initialDatasetType);
  }, [initialDatasetType]);
  
  // Loading state for fullscreen overlay
  const [isLoadingPrimary, setIsLoadingPrimary] = useState(false);
  const [isLoadingBackcheck, setIsLoadingBackcheck] = useState(false);
  const [isLoadingXLSForm, setIsLoadingXLSForm] = useState(false);
 
  // Transcribe modal state
  const [isTranscribeOpen, setIsTranscribeOpen] = useState(false);
  const [isUploadingMerged, setIsUploadingMerged] = useState(false);
  const [tauriApi, setTauriApi] = useState(null);
 
  // Persisted record of the most recent successful transcription on this project.
  // Shape: { filesProcessed, columnsAdded, completedAt (ISO) } | null
  const [lastTranscription, setLastTranscription] = useState(null);
 
  // Hydrate lastTranscription from localStorage when projectId resolves
  useEffect(() => {
    if (!projectId) return;
    try {
      const raw = localStorage.getItem(lastTranscriptionKey(projectId));
      if (raw) setLastTranscription(JSON.parse(raw));
      else setLastTranscription(null);
    } catch (e) {
      console.warn('Could not read lastTranscription from localStorage:', e);
    }
  }, [projectId]);
 
  // Backfill: detect transcript columns in primary data when no localStorage
  // record exists for this project. Runs after primary data loads or projectId
  // changes. This catches projects transcribed before the persistence code
  // shipped, or after a browser cache clear / different machine.
  //
  // Heuristic: if any column name ends with "_transcript", we infer that a
  // prior transcription happened. We can't recover the exact completedAt or
  // filesProcessed without server-side state, so we use sensible inferred
  // values (column count is real; completedAt set to "earlier" with a null
  // marker so the relative-time formatter shows "earlier" instead of "just now").
  useEffect(() => {
    if (!projectId) return;
    if (lastTranscription) return; // already have a record, don't overwrite
    if (!primaryData?.columns) return;
 
    // Normalize columns to string array (handles both string[] and {name}[] shapes)
    const colNames = primaryData.columns.map(c =>
      typeof c === 'string' ? c : (c?.name || c?.column || '')
    ).filter(Boolean);
    const transcriptCols = colNames.filter(n => /_transcript$/i.test(n));
 
    if (transcriptCols.length === 0) return; // no prior transcripts detected
 
    const inferredRecord = {
      filesProcessed: null,                   // unknown — we never persisted it
      columnsAdded: transcriptCols.length,    // real count from current schema
      completedAt: null,                      // unknown — formatter renders "earlier"
      inferred: true,                         // marker so we know this isn't a fresh record
    };
    setLastTranscription(inferredRecord);
    try {
      localStorage.setItem(lastTranscriptionKey(projectId), JSON.stringify(inferredRecord));
    } catch (e) {
      console.warn('Could not persist inferred lastTranscription:', e);
    }
  }, [projectId, primaryData, lastTranscription]);
 
  // Resolve Tauri API once on mount (desktop only)
  useEffect(() => {
    if (!isTauri) return;
    let mounted = true;
    (async () => {
      const api = await getTauriApi();
      if (mounted) setTauriApi(api);
    })();
    return () => { mounted = false; };
  }, []);
 
  // After transcription completes, re-upload the merged CSV as the project primary
  const handleTranscribeComplete = async (payload) => {
    if (!payload?.outputPath) return;
    if (!projectId) {
      console.warn('No projectId; cannot replace primary.');
      return;
    }
 
    setIsUploadingMerged(true);
    try {
      const api = tauriApi || (await getTauriApi());
      if (!api?.invoke) {
        throw new Error('Tauri API not available; cannot read merged CSV.');
      }
 
      // Read the merged CSV bytes via our custom Rust command (read_file_bytes).
      // Tauri serializes Vec<u8> as a JSON number array which JS receives as a
      // regular array. Build a File from it, then re-upload via DataService.
      const bytesArray = await api.invoke('read_file_bytes', {
        path: payload.outputPath,
      });
      const uint8 = new Uint8Array(bytesArray);
 
      const filename = payload.outputPath.split(/[\\/]/).pop() || 'merged.csv';
      const file = new File([uint8], filename, { type: 'text/csv' });
 
      // Re-upload as primary — replaces project's primary CSV in backend.
      // file_type='csv' is the file format; backend defaults data_file_type='primary'
      const loadedData = await DataService.loadData(file, 'csv', 1, 100, projectId);
 
      if (onDataLoaded) {
        onDataLoaded(loadedData);
      }
 
      // Persist transcription metadata for the post-Apply ribbon state.
      // TranscribeModal sends payload.counters.done and payload.transcriptColumnsAdded (array).
      const filesProcessed = payload?.counters?.done
        ?? payload?.filesProcessed
        ?? null;
      const columnsAdded = Array.isArray(payload?.transcriptColumnsAdded)
        ? payload.transcriptColumnsAdded.length
        : (payload?.transcriptColumnsAdded ?? null);
      const record = {
        filesProcessed,
        columnsAdded,
        completedAt: new Date().toISOString(),
      };
      setLastTranscription(record);
      try {
        if (projectId) {
          localStorage.setItem(lastTranscriptionKey(projectId), JSON.stringify(record));
        }
      } catch (e) {
        console.warn('Could not persist lastTranscription to localStorage:', e);
      }
 
      console.log('✓ Merged CSV applied to project. New columns:',
        payload.transcriptColumnsAdded);
    } catch (e) {
      console.error('Failed to apply merged CSV to project:', e);
      alert(`Could not replace project primary with merged CSV.\n\n${e.message || e}`);
    } finally {
      setIsUploadingMerged(false);
    }
  };
 
  // Colors — cohesive blue-teal palette
  const colors = {
    pageBg: isDarkMode ? '#0D0D0E' : '#F8F5EE',
    cardBg: isDarkMode ? '#141516' : '#ffffff',
    cardBgHover: isDarkMode ? '#1A1B1C' : '#fafafa',
    surfaceBg: isDarkMode ? '#18181b' : 'rgba(255, 255, 255, 0.5)',
    border: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    borderLight: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.05)',
    borderAccent: isDarkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.16)',
    text: isDarkMode ? '#f0ede4' : '#1a1a1a',
    textSecondary: isDarkMode ? '#a8a49a' : '#555550',
    textMuted: isDarkMode ? '#6b6862' : '#908c82',
    primary: '#2563EB',           // brand blue
    primaryHover: '#1d4ed8',
    primaryBg: isDarkMode ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.08)',
    secondary: '#D97706',         // brand amber
    success: '#2563EB',           // matches pc-status complete
    successBg: isDarkMode ? 'rgba(4, 120, 87, 0.15)' : 'rgba(4, 120, 87, 0.08)',
    successBorder: isDarkMode ? 'rgba(4, 120, 87, 0.35)' : 'rgba(4, 120, 87, 0.25)',
    warning: '#b45309',
    warningBg: isDarkMode ? 'rgba(180, 83, 9, 0.15)' : 'rgba(180, 83, 9, 0.08)',
    error: '#dc2626',
    errorBg: isDarkMode ? 'rgba(220, 38, 38, 0.12)' : 'rgba(220, 38, 38, 0.08)',
    // Dataset accents — all resolve to brand blue; icon bg uses a Quad-motif
    // gradient, applied via CSS rather than this token.
    datasetPrimary: '#2563EB',
    datasetXlsform: '#2563EB',
    datasetBackcheck: '#2563EB',
  };
 
  // Dataset options for card selection
  const datasetOptions = [
    {
      id: 'primary',
      name: 'Primary Survey',
      icon: Table,
      description: 'Main survey dataset for quality analysis',
      color: colors.datasetPrimary,
      loaded: primaryDataLoaded,
      required: true,
      stats: primaryData ? {
        rows: primaryData.total_rows || (primaryData.data?.length) || 0,
        columns: primaryData.total_variables || (primaryData.columns?.length) || 0
      } : null
    },
    {
      id: 'xlsform',
      name: 'XLSForm',
      icon: FileSpreadsheet,
      description: 'Form definition for skip logic validation',
      color: colors.datasetXlsform,
      loaded: xlsformLoaded,
      required: false,
      stats: xlsformDataProp?.parseResult?.summary ? {
        questions: xlsformDataProp.parseResult.summary.total_questions || 0,
        skipLogic: xlsformDataProp.parseResult.summary.questions_with_skip_logic || 0
      } : null
    },
    {
      id: 'backcheck',
      name: 'Back-Check Dataset',
      icon: Layers,
      description: 'Verification data for comparison analysis',
      color: colors.datasetBackcheck,
      loaded: backcheckDataLoaded,
      required: false,
      stats: backcheckDataProp ? {
        rows: backcheckDataProp.total_rows || (backcheckDataProp.data?.length) || 0,
        columns: backcheckDataProp.total_variables || (backcheckDataProp.columns?.length) || 0
      } : null
    }
  ];
 
  const handlePrimaryDataLoaded = (loadedData) => {
    console.log('Primary data loaded:', loadedData);
    setIsLoadingPrimary(false);
    if (onDataLoaded) {
      onDataLoaded(loadedData);
    }
  };
 
  const handleBackcheckDataLoaded = (loadedData) => {
    console.log('Backcheck data loaded:', loadedData);
    setIsLoadingBackcheck(false);
    if (onBackcheckDataLoaded) {
      onBackcheckDataLoaded(loadedData);
    }
  };
 
  const handleXLSFormLoaded = (xlsformResult) => {
    console.log('XLSForm loaded:', xlsformResult);
    setIsLoadingXLSForm(false);
    if (onXLSFormLoaded) {
      onXLSFormLoaded(xlsformResult);
    }
  };
 
  const handleClearPreviousState = () => {
    if (onClearPreviousState) {
      onClearPreviousState();
    }
  };
 
  // Get stats display text
  const getStatsDisplay = (dataset) => {
    if (!dataset.stats) return dataset.required ? 'Required' : 'Optional';
    
    if (dataset.id === 'xlsform') {
      return `${dataset.stats.questions} questions • ${dataset.stats.skipLogic} skip rules`;
    }
    return `${dataset.stats.rows?.toLocaleString()} rows • ${dataset.stats.columns} columns`;
  };
 
  return (
    <div 
      className={`data-loader-page ${isDarkMode ? 'dark' : 'light'}`}
      style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: 'transparent',
        padding: '24px 32px 80px',
      }}
    >
      {/* FULLSCREEN LOADING OVERLAYS */}
      <AILoadingOverlay
        isVisible={isLoadingPrimary}
        context="data_loading"
        title="Loading Primary Dataset"
        subtitle="Parsing and validating your survey data"
        showDetailedLogs={true}
      />
      <AILoadingOverlay
        isVisible={isLoadingBackcheck}
        context="data_loading"
        title="Loading Backcheck Dataset"
        subtitle="Preparing verification data for comparison"
        showDetailedLogs={true}
      />
      <AILoadingOverlay
        isVisible={isLoadingXLSForm}
        context="data_loading"
        title="Parsing XLSForm"
        subtitle="Extracting skip logic and form structure"
        showDetailedLogs={true}
      />
 
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* Header — Projects style */}
        <div className="dl-header">
          <div className="dl-header-left">
            <div className="dl-header-icon">
              <Database size={24} />
            </div>
            <h1 className="dl-title">Data Sources</h1>
          </div>
        </div>
 
        {/* Tabs — Primary Survey | XLSForm | Back-Check Dataset */}
        <div className="dl-tabs-container">
          <div className="dl-tabs">
            {datasetOptions.map(dataset => {
              const Icon = dataset.icon;
              const isActive = activeDataset === dataset.id;
              return (
                <button 
                  key={dataset.id}
                  className={`dl-tab ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveDataset(dataset.id)}
                >
                  <Icon size={14} />
                  {dataset.name}
                  {dataset.loaded ? (
                    <span className="dl-tab-badge ready">
                      <CheckCircle size={10} />
                      Ready
                    </span>
                  ) : (
                    !dataset.required && (
                      <span className="dl-tab-badge">Optional</span>
                    )
                  )}
                </button>
              );
            })}
          </div>
          
          <div className="dl-tabs-actions">
            {/* Status summary inline */}
            <span style={{ fontSize: '12px', color: colors.textMuted }}>
              {[primaryDataLoaded, xlsformLoaded, backcheckDataLoaded].filter(Boolean).length}/3 connected
            </span>
          </div>
        </div>
 
        {/* Import Panel — Primary Data */}
        {activeDataset === 'primary' && (
          <div className="dl-panel with-nodes">
            <span className="cn-bl" /><span className="cn-br" />
            <div className="dl-panel-header">
              <div className="dl-panel-icon">
                <Table size={18} />
              </div>
              <div>
                <h3 className="dl-panel-title">Primary Survey Data</h3>
                <p className="dl-panel-subtitle">Main dataset for quality analysis and validation</p>
              </div>
              {primaryDataLoaded && primaryData && (
                <div className="dl-panel-stats">
                  {(primaryData.total_rows || primaryData.data?.length || 0).toLocaleString()} rows • {primaryData.total_variables || primaryData.columns?.length || 0} columns
                </div>
              )}
            </div>
 
            {/* Transcribe Audio ribbon — primary card add-on */}
            {primaryDataLoaded && (() => {
              const hasRecord = Boolean(lastTranscription?.completedAt);
              const accent = hasRecord ? '#2563EB' : '#2563EB';                    // success vs primary blue
              const accentBg = hasRecord
                ? (isDarkMode ? 'rgba(4, 120, 87, 0.10)' : 'rgba(4, 120, 87, 0.06)')
                : (isDarkMode ? 'rgba(37, 99, 235, 0.06)' : 'rgba(37, 99, 235, 0.04)');
              const accentBorder = hasRecord
                ? (isDarkMode ? 'rgba(4, 120, 87, 0.28)' : 'rgba(4, 120, 87, 0.22)')
                : (isDarkMode ? 'rgba(37, 99, 235, 0.18)' : 'rgba(37, 99, 235, 0.16)');
              const iconBg = hasRecord
                ? 'rgba(4, 120, 87, 0.15)'
                : 'rgba(37, 99, 235, 0.15)';
 
              const title = hasRecord ? 'Audio transcribed' : 'Transcribe audio responses';
              const subtitle = hasRecord
                ? (lastTranscription.inferred
                    ? `${lastTranscription.columnsAdded} transcript column${lastTranscription.columnsAdded !== 1 ? 's' : ''} detected in primary • ${formatRelativeTime(lastTranscription.completedAt)}`
                    : `${lastTranscription.filesProcessed ?? '—'} files • ${lastTranscription.columnsAdded ?? '—'} transcript columns added • ${formatRelativeTime(lastTranscription.completedAt)}`)
                : (isTauri
                    ? 'Add transcript columns to your dataset by running Whisper on local audio files.'
                    : 'Available in DataQuad Desktop — run Whisper locally on your audio files.');
              const buttonLabel = isUploadingMerged
                ? 'Applying...'
                : (!tauriApi ? 'Loading...' : (hasRecord ? 'Run again' : 'Transcribe audio'));
 
              return (
              <div style={{
                margin: '0 24px',
                padding: '14px 16px',
                background: accentBg,
                border: `1px solid ${accentBorder}`,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <div style={{
                  width: '32px', height: '32px',
                  borderRadius: '50%',
                  background: iconBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: accent, flexShrink: 0,
                }}>
                  {hasRecord ? <CheckCircle2 size={16} /> : <Headphones size={16} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: colors.text }}>
                    {title}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}>
                    {subtitle}
                  </div>
                </div>
                {isTauri ? (
                  <button
                    onClick={() => setIsTranscribeOpen(true)}
                    disabled={isUploadingMerged || !tauriApi}
                    style={{
                      padding: '8px 14px',
                      background: hasRecord ? 'transparent' : '#2563EB',
                      color: hasRecord ? accent : '#FFFFFF',
                      border: hasRecord ? `1px solid ${accentBorder}` : 'none',
                      borderRadius: '8px',
                      fontSize: '12.5px',
                      fontWeight: 500,
                      cursor: (isUploadingMerged || !tauriApi) ? 'not-allowed' : 'pointer',
                      opacity: (isUploadingMerged || !tauriApi) ? 0.6 : 1,
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {hasRecord && !isUploadingMerged && tauriApi && <RotateCw size={12} />}
                    {buttonLabel}
                  </button>
                ) : (
                  <a
                    href="https://dataquadapp.com/download"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '8px 14px',
                      background: 'transparent',
                      color: '#2563EB',
                      border: `1px solid ${isDarkMode ? 'rgba(37, 99, 235, 0.4)' : 'rgba(37, 99, 235, 0.4)'}`,
                      borderRadius: '8px',
                      fontSize: '12.5px',
                      fontWeight: 500,
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexShrink: 0,
                    }}
                  >
                    <Download size={12} />
                    Get Desktop
                  </a>
                )}
              </div>
              );
            })()}
 
            <div style={{ padding: '24px' }}>
              <SelectPrimaryData
                onDataLoaded={handlePrimaryDataLoaded}
                isDataPreviewVisible={primaryDataLoaded}
                onClearPreviousState={handleClearPreviousState}
                projectId={projectId}
                onLoadingStart={() => setIsLoadingPrimary(true)}
                onLoadingEnd={() => setIsLoadingPrimary(false)}
              />
            </div>
          </div>
        )}
 
        {/* Import Panel — XLSForm */}
        {activeDataset === 'xlsform' && (
          <div className="dl-panel with-nodes">
            <span className="cn-bl" /><span className="cn-br" />
            <div className="dl-panel-header">
              <div className="dl-panel-icon">
                <FileSpreadsheet size={18} />
              </div>
              <div>
                <h3 className="dl-panel-title">XLSForm Definition</h3>
                <p className="dl-panel-subtitle">Upload your form definition for skip logic validation</p>
              </div>
              {xlsformLoaded && xlsformDataProp?.parseResult?.summary && (
                <div className="dl-panel-stats">
                  {xlsformDataProp.parseResult.summary.total_questions || 0} questions • {xlsformDataProp.parseResult.summary.questions_with_skip_logic || 0} skip rules
                </div>
              )}
            </div>
            <div style={{ padding: '24px' }}>
              <LoadXLSForm
                onXLSFormLoaded={handleXLSFormLoaded}
                isXLSFormLoaded={xlsformLoaded}
                dataColumns={dataColumns}
                projectId={projectId}
                xlsformData={xlsformDataProp}
                onLoadingStart={() => setIsLoadingXLSForm(true)}
                onLoadingEnd={() => setIsLoadingXLSForm(false)}
              />
            </div>
          </div>
        )}
 
        {/* Import Panel — Back-Check */}
        {activeDataset === 'backcheck' && (
          <div className="dl-panel with-nodes">
            <span className="cn-bl" /><span className="cn-br" />
            <div className="dl-panel-header">
              <div className="dl-panel-icon">
                <Layers size={18} />
              </div>
              <div>
                <h3 className="dl-panel-title">Back-Check Dataset</h3>
                <p className="dl-panel-subtitle">Optional verification data for comparison analysis</p>
              </div>
              {backcheckDataLoaded && backcheckDataProp && (
                <div className="dl-panel-stats">
                  {(backcheckDataProp.total_rows || backcheckDataProp.data?.length || 0).toLocaleString()} rows • {backcheckDataProp.total_variables || backcheckDataProp.columns?.length || 0} columns
                </div>
              )}
            </div>
            <div style={{ padding: '24px' }}>
              <SelectBackCheckData
                onDataLoaded={handleBackcheckDataLoaded}
                isDataPreviewVisible={backcheckDataLoaded}
                onClearPreviousState={() => {}}
                projectId={projectId}
                onLoadingStart={() => setIsLoadingBackcheck(true)}
                onLoadingEnd={() => setIsLoadingBackcheck(false)}
              />
            </div>
          </div>
        )}
 
        {/* Navigation Footer — Sticky */}
        {primaryDataLoaded && (
          <div className="dl-continue-bar" style={{
            position: 'sticky',
            bottom: 0,
            marginTop: '20px',
            padding: '14px 22px',
            backgroundColor: isDarkMode ? 'rgba(20, 21, 22, 0.92)' : 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderRadius: '10px',
            border: `0.5px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 10,
            boxShadow: isDarkMode
              ? '0 -4px 24px rgba(0, 0, 0, 0.35)'
              : '0 -4px 24px rgba(0, 0, 0, 0.04)',
          }}>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>
              {primaryDataLoaded && xlsformLoaded && backcheckDataLoaded 
                ? 'All datasets ready for analysis'
                : primaryDataLoaded && xlsformLoaded
                  ? 'Primary data and XLSForm ready'
                  : primaryDataLoaded && backcheckDataLoaded
                    ? 'Primary and back-check data ready'
                    : 'Primary data ready for analysis'}
            </div>
            <button
              onClick={() => onNavigateToPreview && onNavigateToPreview('datapreview')}
              className="dl-continue-btn"
            >
              <span>Continue to Data Preview</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        )}
      </div>
 
      {/* Transcribe modal — desktop-only, mounts when opened.
          Modal auto-exports project primary data via DataService.getProjectDataFull
          regardless of original source format (CSV / XLSX / DTA / SQLite / API / DB). */}
      {isTauri && (
        <TranscribeModal
          isOpen={isTranscribeOpen}
          onClose={() => setIsTranscribeOpen(false)}
          onComplete={handleTranscribeComplete}
          projectId={projectId}
          primaryData={primaryData}
          xlsformData={xlsformDataProp}
          tauriApi={tauriApi}
        />
      )}
    </div>
  );
};
 
export default DataLoader;